import { useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { lastBrandAccountIdAtom } from '~/atoms'
import { ErrorPanel } from '~/components/features/ErrorPanel'
import { useAllBrandAccountsQuery } from '~/hooks/useAllBrandAccountsQuery'
import { useBrandAccountSurveysQuery } from '~/hooks/useBrandAccountSurveysQuery'
import {
  useCommonQuery,
  useCommonQueryBrandAccounts,
} from '~/hooks/useCommonQuery'
import { useCurrentSurvey } from '~/hooks/useCurrentSurvey'
import { useInvalidateCommonQueryOnAuth0UserUpdateEffect } from '~/hooks/useInvalidateCommonQueryOnAuth0UserUpdateEffect'
import { useMeQuery } from '~/hooks/useMeQuery'
import { useSetLastVisitedEffect } from '~/hooks/useSetLastVisitedEffect'
import { useSetSurveyFilterKeyEffect } from '~/hooks/useSetSurveyFilterKeyEffect'
import { useSetSurveyGroupFilterKeyEffect } from '~/hooks/useSetSurveyGroupFilterKeyEffect'
import { LoadingPage } from '~/pages/LoadingPage'
import { findByPath } from '~/utils'

export type InitialTasksProps = {
  children: React.ReactNode
}
export const InitialTasks = ({ children }: InitialTasksProps) => {
  const { t } = useTranslation()
  const { isLoading, isLoadingError } = useCommonQuery()
  const { isLoading: isLoadingMe, isLoadingError: isLoadingErrorMe } =
    useMeQuery()
  const {
    isLoading: isLoadingAllBrandAccounts,
    isLoadingError: isLoadingErrorAllBrandAccounts,
  } = useAllBrandAccountsQuery()
  const currentSurvey = useCurrentSurvey()
  const isLoadingCurrentSurvey =
    !currentSurvey.success && currentSurvey.reason === 'loading'

  useSetLastVisitedEffect()
  useInvalidateCommonQueryOnAuth0UserUpdateEffect()
  useSetSurveyFilterKeyEffect()
  useSetSurveyGroupFilterKeyEffect()

  const lastBrandAccountId = useAtomValue(lastBrandAccountIdAtom)

  const brandAccounts = useCommonQueryBrandAccounts()

  const lastBrandAccount = findByPath(brandAccounts, 'id', lastBrandAccountId)

  const {
    isLoading: isLoadingAvailableSurveys,
    isLoadingError: isLoadingErrorAvailableSurveys,
  } = useBrandAccountSurveysQuery(lastBrandAccount?.id)

  if (
    isLoading ||
    isLoadingMe ||
    isLoadingAllBrandAccounts ||
    isLoadingCurrentSurvey ||
    isLoadingAvailableSurveys
  ) {
    return <LoadingPage />
  }

  // 初回取得に失敗し、フォールバックできるキャッシュも無い場合のみエラーを表示する。
  // isErrorではなくisLoadingError(=データ未取得のエラー)で判定することで、
  // キャッシュ保持中のクエリの背景refetch失敗でアプリ全体が落ちるのを防ぐ。
  if (
    isLoadingError ||
    isLoadingErrorMe ||
    isLoadingErrorAllBrandAccounts ||
    isLoadingErrorAvailableSurveys
  ) {
    return <ErrorPanel message={t('an_error_occurred')} />
  }

  return <>{children}</>
}
